import * as Sentry from '@sentry/react';
// eslint-disable-next-line import/no-unresolved
import React from 'react';
import ReactDOM from 'react-dom';
import { HashRouter as Router } from 'react-router-dom';
import { Provider as ReduxProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import 'long-press-event';

import config from './common/utils/constants/config';
import App from './App';
import { apiClient } from './common/services/api';
import { rewriteGLJSUrlParams } from './common/utils/helpers/location-sync';
import { ApiProvider } from './common/contexts/api-context';
import { AppErrorProvider } from './common/contexts/app-error-context';
import { initStore } from './app/store/init';

import './styles.css';

// local testing only
// eslint-disable-next-line no-undef
// window.MapboxPageShellProduction = true;
initializeMapboxAnalytics({
  segmentIntegrations: {
    'Customer.io': false,
  },
});

if (config.SENTRY_DSN) {
  Sentry.init({
    dsn: config.SENTRY_DSN as string,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        minReplayDuration: 1000,
        networkDetailAllowUrls: [
          config.CONTRIBUTE_API_URL as string,
          config.CONTRIBUTE_PRIVATE_API_URL as string,
          'https://api.mapbox.com/search/',
        ],
        networkCaptureBodies: true,
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
      }),
      // Enable canvas recording
      Sentry.replayCanvasIntegration(),
    ],
    // adjust in case of too many samples
    tracesSampleRate: 0.2,

    // This sets the sample rate to be 0%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0,

    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1.0,
  });
}

/**
 * Throw error without blocking scripts execution.
 * Error is printed in console, processed and reported by sentry.
 */
const asyncError = (msg) => {
  setTimeout(() => {
    throw new Error(msg);
  }, 0);
};

if (!config.CONTRIBUTE_API_URL) {
  asyncError('CONTRIBUTE_API_URL is undefined');
}

analytics.track('Application Start');

// Can miss some events. @see https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event#usage_notes
// eslint-disable-next-line no-unused-vars
window.addEventListener('beforeunload', () => {
  analytics.track('Application End');
});

const container = document.getElementById('root');

const { store, persistor } = initStore();

rewriteGLJSUrlParams();

ReactDOM.render(
  <Router basename={config.BASE_URL_PATH as string}>
    <ApiProvider value={apiClient}>
      <AppErrorProvider>
        <ReduxProvider store={store}>
          <PersistGate persistor={persistor}>
            <App />
          </PersistGate>
        </ReduxProvider>
      </AppErrorProvider>
    </ApiProvider>
  </Router>,
  container,
);
